import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Tag, Space, Typography, Modal, Form, Input, message, Row, Col, Alert, Tooltip, Badge, Tabs, Collapse, Select, Switch } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  EyeOutlined,
  ReloadOutlined,
  ExportOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  DollarOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { handleApiError } from '../../utils/errorHandler';
import {
  customerNameRules,
  contactPersonRules,
  emailRules,
  phoneRules,
  addressRules,
  taxNumberRules,
  usernameRules,
  fullNameRules,
  passwordRules,
  passwordHelpText,
} from '../../utils/validationRules';
import logger from '../../services/logger';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Option } = Select;

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    withDevices: 0,
    withInvoices: 0
  });

  useEffect(() => {
    fetchCustomers();
    fetchCustomerUsers();
    fetchPasswordPolicy();
  }, []);

  const fetchPasswordPolicy = async () => {
    try {
      const response = await axios.get('/api/v1/profile/password-requirements');
      setPasswordPolicy(response.data);
    } catch (error) {
      logger.error('Password policy fetch error:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/customers');
      const customersData = Array.isArray(response.data) ? response.data : [];
      setCustomers(customersData);

      // Calculate statistics
      const customerStats = {
        total: customersData.length,
        active: customersData.filter(c => c.is_active).length,
        inactive: customersData.filter(c => !c.is_active).length,
        withDevices: customersData.filter(c => c.device_count > 0).length,
        withInvoices: customersData.filter(c => c.invoice_count > 0).length
      };
      setStats(customerStats);
    } catch (error) {
      handleApiError(error, 'Müşteriler yüklenirken hata oluştu', message.error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerUsers = async () => {
    try {
      const response = await axios.get('/api/v1/users');
      // Sadece customer rolündeki kullanıcıları filtrele
      const allUsers = Array.isArray(response.data) ? response.data : [];
      const onlyCustomerUsers = allUsers.filter(u => u.role?.name === 'customer');
      setCustomerUsers(onlyCustomerUsers);
    } catch (error) {
      logger.error('Customer users fetch error:', error);
      // Sessizce devam et
    }
  };

  const handleAdd = () => {
    setEditingCustomer(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCustomer(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Müşteriyi silmek istediğinizden emin misiniz?',
      content: 'Bu işlem geri alınamaz. Müşteriye ait tüm cihazlar ve faturalar da etkilenebilir.',
      okText: 'Evet, Sil',
      cancelText: 'İptal',
      okType: 'danger',
      onOk: async () => {
        try {
          await axios.delete(`/api/v1/customers/${id}`);
          message.success('Müşteri başarıyla silindi');
          fetchCustomers();
        } catch (error) {
          handleApiError(error, 'Müşteri silinirken hata oluştu', message.error);
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCustomer) {
        await axios.put(`/api/v1/customers/${editingCustomer.id}`, values);
        message.success('Müşteri başarıyla güncellendi');
      } else {
        await axios.post('/api/v1/customers', values);
        message.success('Müşteri başarıyla eklendi');
      }
      setModalVisible(false);
      fetchCustomers();
    } catch (error) {
      handleApiError(error, 'İşlem başarısız', message.error);
    }
  };

  // Filtreleme fonksiyonu
  const getFilteredCustomers = () => {
    const customersArray = Array.isArray(customers) ? customers : [];
    if (!searchText) return customersArray;
    
    return customersArray.filter(customer => {
      const searchLower = searchText.toLowerCase();
      return (
        customer.name?.toLowerCase().includes(searchLower) ||
        customer.contact_person?.toLowerCase().includes(searchLower) ||
        customer.contact_email?.toLowerCase().includes(searchLower) ||
        customer.contact_phone?.toLowerCase().includes(searchLower)
      );
    });
  };



  const columns = [
    {
      title: 'Müşteri Adı',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <UserOutlined style={{ color: record.is_active ? '#52c41a' : '#d9d9d9' }} />
          <span style={{ fontWeight: 'bold' }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'İletişim Kişisi',
      dataIndex: 'contact_person',
      key: 'contact_person',
      render: (person) => person || '-',
    },
    {
      title: 'E-posta',
      dataIndex: 'contact_email',
      key: 'contact_email',
      render: (email) => email ? <a href={`mailto:${email}`}>{email}</a> : '-',
    },
    {
      title: 'Telefon',
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      render: (phone) => phone ? <a href={`tel:${phone}`}>{phone}</a> : '-',
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Badge 
          status={active ? 'success' : 'error'} 
          text={active ? 'Aktif' : 'Pasif'} 
        />
      ),
      filters: [
        { text: 'Aktif', value: true },
        { text: 'Pasif', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: 'Cihaz Sayısı',
      dataIndex: 'device_count',
      key: 'device_count',
      render: (count) => (
        <Tag color={count > 0 ? 'blue' : 'default'} icon={<SettingOutlined />}>
          {count || 0}
        </Tag>
      ),
      sorter: (a, b) => (a.device_count || 0) - (b.device_count || 0),
    },
    {
      title: 'Fatura Sayısı',
      dataIndex: 'invoice_count',
      key: 'invoice_count',
      render: (count) => (
        <Tag color={count > 0 ? 'green' : 'default'} icon={<DollarOutlined />}>
          {count || 0}
        </Tag>
      ),
      sorter: (a, b) => (a.invoice_count || 0) - (b.invoice_count || 0),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Profil Detayları">
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => navigate(`/customers/${record.id}`)}
              type="text"
            />
          </Tooltip>
          <Tooltip title="Düzenle">
            <Button 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
              type="text"
            />
          </Tooltip>
          <Tooltip title="Sil">
            <Button 
              icon={<DeleteOutlined />} 
              danger 
              onClick={() => handleDelete(record.id)}
              type="text"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <TeamOutlined /> Müşteriler
        </Title>
      </div>

      {/* Customer Stats Summary */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <Space size="large" wrap>
            <span>
              <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Toplam</Text>
              <Text strong style={{ fontSize: 18 }}>{stats.total}</Text>
            </span>
            <span>
              <Badge status="success" />
              <Text style={{ marginRight: 4 }}>Aktif</Text>
              <Text strong style={{ color: '#3f8600', fontSize: 18 }}>{stats.active}</Text>
            </span>
            {stats.inactive > 0 && (
              <span>
                <Badge status="warning" />
                <Text style={{ marginRight: 4 }}>Pasif</Text>
                <Text strong style={{ color: '#fa8c16', fontSize: 18 }}>{stats.inactive}</Text>
              </span>
            )}
          </Space>
          <Space size="middle" wrap>
            <span>
              <SettingOutlined style={{ color: '#1890ff', marginRight: 4 }} />
              <Text style={{ marginRight: 4 }}>Cihazlı</Text>
              <Text strong style={{ fontSize: 16 }}>{stats.withDevices}</Text>
            </span>
            {stats.withInvoices > 0 && (
              <span>
                <DollarOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                <Text style={{ marginRight: 4 }}>Faturalı</Text>
                <Text strong style={{ fontSize: 16 }}>{stats.withInvoices}</Text>
              </span>
            )}
            <span>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
              <Text style={{ marginRight: 4 }}>Aktiflik</Text>
              <Text strong style={{ fontSize: 18 }}>
                {stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}%
              </Text>
            </span>
          </Space>
        </div>
      </Card>

             {/* Alerts */}
       {stats.inactive > 0 && (
         <Alert
           message={`${stats.inactive} pasif müşteri bulunmaktadır`}
           description="Bu müşterilerin durumlarını kontrol etmeyi unutmayın."
           type="warning"
           showIcon
           style={{ marginBottom: 16 }}
         />
       )}

       {/* Action Buttons and Search */}
       <Card style={{ marginBottom: 24 }}>
         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
           {/* Sol taraf - Butonlar */}
           <Space>
             <Button 
               type="primary" 
               icon={<PlusOutlined />} 
               onClick={handleAdd}
             >
               Yeni Müşteri Ekle
             </Button>
             <Button 
               icon={<ReloadOutlined />} 
               onClick={fetchCustomers}
               loading={loading}
             >
               Yenile
             </Button>
             <Button icon={<ExportOutlined />}>
               Dışa Aktar
             </Button>
           </Space>

           {/* Sağ taraf - Arama */}
           <div style={{ minWidth: 400 }}>
             <Input.Search
               placeholder="Müşteri adı, iletişim kişisi, e-posta veya telefon ara..."
               allowClear
               value={searchText}
               onChange={(e) => setSearchText(e.target.value)}
               style={{ width: '100%', minWidth: 300 }}
             />
           </div>
         </div>
       </Card>

       {/* Customers Table */}
       <Card>
          <Table
            columns={columns}
            dataSource={getFilteredCustomers()}
            rowKey="id"
            loading={loading}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} müşteri`,
            }}
            rowClassName={(record) => !record.is_active ? 'table-row-disabled' : ''}
          />
        </Card>

      <Modal
        title={editingCustomer ? 'Müşteri Düzenle' : 'Yeni Müşteri Ekle'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose={true}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Müşteri Adı"
                rules={customerNameRules()}
              >
                <Input prefix={<TeamOutlined />} placeholder="Örn: ABC Şirketi Ltd." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contact_person"
                label="İletişim Kişisi"
                rules={contactPersonRules({ required: true })}
              >
                <Input prefix={<UserOutlined />} placeholder="Ad Soyad" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contact_email"
                label="E-posta"
                rules={emailRules({ required: true })}
              >
                <Input prefix={<MailOutlined />} placeholder="iletisim@sirket.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contact_phone"
                label="Telefon"
                rules={phoneRules({ required: true })}
              >
                <Input prefix={<PhoneOutlined />} placeholder="+90 555 123 4567" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Adres"
            rules={addressRules({ required: true })}
          >
            <Input.TextArea rows={3} placeholder="Tam adres" />
          </Form.Item>

          <Form.Item
            name="tax_number"
            label="Vergi Numarası"
            rules={taxNumberRules()}
          >
            <Input placeholder="Opsiyonel" />
          </Form.Item>

          {/* Kullanıcı Yönetimi Bölümü - sadece yeni müşteri oluştururken */}
          {!editingCustomer && (
            <Collapse
              style={{ marginTop: 24, marginBottom: 24 }}
              bordered={false}
            >
              <Panel
                header={
                  <Space>
                    <UserOutlined />
                    <span style={{ fontWeight: 'bold' }}>Kullanıcı Atamaları</span>
                    <Tag color="blue">Opsiyonel</Tag>
                  </Space>
                }
                key="1"
              >
                <Tabs defaultActiveKey="existing">
                  <TabPane tab="Mevcut Kullanıcılar" key="existing">
                    <Alert
                      message="Mevcut Kullanıcı Seçimi"
                      description="Bu müşteriye erişimi olacak mevcut customer rolündeki kullanıcıları seçin."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item
                      name="user_ids"
                      label="Müşteri Kullanıcıları"
                      help="Bu müşteriye erişimi olacak kullanıcıları seçin (birden fazla seçilebilir)"
                    >
                      <Select
                        mode="multiple"
                        placeholder="Kullanıcı seçin"
                        showSearch
                        filterOption={(input, option) =>
                          (option.label || '').toLowerCase().includes(input.toLowerCase())
                        }
                        maxTagCount={3}
                        maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} kullanıcı`}
                        options={customerUsers.map(u => ({
                          value: u.id,
                          label: `${u.username} (${u.full_name || 'İsim yok'})`,
                          disabled: !u.is_active
                        }))}
                      />
                    </Form.Item>
                  </TabPane>

                  <TabPane tab="Yeni Kullanıcı Oluştur" key="new">
                    <Alert
                      message="Yeni Müşteri Kullanıcısı"
                      description="Bu müşteri için özel kullanıcı oluşturun. Role otomatik 'customer' atanacaktır."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />

                    <Form.List name="new_users">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <Card
                              key={key}
                              size="small"
                              title={`Kullanıcı ${key + 1}`}
                              extra={
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(name)}
                                >
                                  Kaldır
                                </Button>
                              }
                              style={{ marginBottom: 16 }}
                            >
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'username']}
                                    label="Kullanıcı Adı"
                                    rules={usernameRules()}
                                  >
                                    <Input placeholder="kullanici_adi" prefix={<UserOutlined />} />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'email']}
                                    label="E-posta"
                                    rules={emailRules({ required: true })}
                                  >
                                    <Input placeholder="email@example.com" prefix={<MailOutlined />} />
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'full_name']}
                                    label="Ad Soyad"
                                    rules={fullNameRules()}
                                  >
                                    <Input placeholder="Ad Soyad" />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'password']}
                                    label="Şifre"
                                    rules={passwordRules(passwordPolicy, { required: true })}
                                    extra={passwordHelpText(passwordPolicy)}
                                  >
                                    <Input.Password placeholder="Güçlü şifre" />
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Form.Item
                                {...restField}
                                name={[name, 'is_active']}
                                label="Hesap Durumu"
                                valuePropName="checked"
                                initialValue={true}
                              >
                                <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                              </Form.Item>
                            </Card>
                          ))}

                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                            style={{ marginTop: 8 }}
                          >
                            Yeni Kullanıcı Ekle
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </TabPane>
                </Tabs>
              </Panel>
            </Collapse>
          )}

          <Form.Item
            name="is_active"
            label="Hesap Durumu"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCustomer ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Customers;
